import { useEffect, useState, useCallback } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/credits";
import { Loader2, LogOut, Circle } from "lucide-react";

export default function Sessions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/sessions"); setRows(data); }
    catch (e) {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const forceLogout = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/users/${id}/force-logout`);
      toast.success(data.message || "User signed out");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <DashboardLayout title="Sessions">
      <div className="mb-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Security</p>
        <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">User sessions</h2>
        <p className="mt-1 text-sm text-neutral-500">Monitor logins and force-logout any account.</p>
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-[#141414]" data-testid="sessions-table">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/[0.06] text-xs uppercase tracking-wider text-neutral-500">
                  <th className="px-6 py-4 font-medium">User</th>
                  <th className="px-6 py-4 font-medium">Role</th>
                  <th className="px-6 py-4 font-medium">Status</th>
                  <th className="px-6 py-4 font-medium">Last Login</th>
                  <th className="px-6 py-4 font-medium">Last IP</th>
                  <th className="px-6 py-4 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-white/[0.04]" data-testid={`session-row-${r.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Circle className={`h-2 w-2 ${r.online ? "fill-emerald-400 text-emerald-400" : "fill-neutral-600 text-neutral-600"}`} />
                        <div><p className="font-medium text-white">{r.business_name}</p><p className="text-xs text-neutral-500">{r.email}</p></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-300">{r.role === "super_admin" ? "Super Admin" : "Installer"}</td>
                    <td className="px-6 py-4 text-sm capitalize text-neutral-400">{r.status?.replace("_", " ")}</td>
                    <td className="px-6 py-4 text-sm text-neutral-400">{r.last_login ? fmtDate(r.last_login, true) : "Never"}</td>
                    <td className="px-6 py-4 font-mono text-xs text-neutral-500">{r.last_ip || "—"}</td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => forceLogout(r.id)} disabled={busy === r.id} className="inline-flex items-center gap-1.5 rounded-lg border border-white/[0.1] px-3 py-1.5 text-xs font-medium text-neutral-300 hover:bg-white/[0.05] disabled:opacity-60" data-testid={`force-logout-${r.id}`}>
                        {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />} Force Logout
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
